import React, {
  useEffect, useRef, useState, ReactNode,
} from 'react';
import styled from 'styled-components';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import { Input, InputCss } from './Input';
import {
  DateFormatStringEnum, initDate, isValidDate,
} from '../../../../utils';
import { OutsideClicker } from '../../OutsideClicker';
import * as NumericFormat from '../../../../utils/numeric-format';
import { useFormConfigurationContext } from '../../../..';

const DatePickerInputContainer = styled.div`
  position: relative;
`;

const DatePickerWrapper = styled.div<{ fluid?: boolean }>`${({
  fluid, theme,
}) => `
  ${fluid && `
  display: flex;
  justify-content: space-between;
  gap: ${theme.padding[5]};
  `};
`}`;

const TextFieldInput = styled.input<{
  styles?: string;
  hasError?: boolean;
  paddingLeft?: number;
  paddingRight?: number;
  small?: boolean;
  xs?: boolean;
  width?: number;
  fluid?: boolean;
}>`
  ${InputCss};
  ${({
    theme, disabled, hasError, xs, small, width,
  }) => `
    background-color: ${xs ? theme.colours.neutral[9] : theme.colours.neutral[10]};
    font-weight: ${theme.fontWeight.normal};
    padding: ${theme.padding[2]} ${xs || small ? `${theme.padding[2]} !important` : theme.padding[3]};
    border-color: ${hasError ? theme.textDanger : theme.colours.neutral[6]};
    width: ${width}px;
    margin-right: 8px;
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    &[type=number] {
      -moz-appearance: textfield;
    }
    ${!disabled && `
      &:hover {
        background-color: ${theme.colours.neutral[10]};
        border-color: ${hasError ? theme.textDanger : theme.colours.primary[5]};
      }
      &:focus {        
        box-shadow: 0px 0px 0px ${theme.padding[1]} ${hasError ? theme.danger : theme.colours.primary[11]};
        background-color: ${theme.colours.neutral[10]};
        border: 1px solid ${hasError ? theme.textDanger : theme.colours.primary[5]};
      }
    `};
    &:disabled {
      background-color: ${theme.colours.neutral[8]};
      border-color: ${theme.colours.neutral[8]};
    }
    &::placeholder {
      color: ${theme.colours.neutral[5]};
      font-size: ${theme.fontSize[2]};
      font-weight: ${theme.fontWeight.normal}
    }
  `};
  ${({ theme, paddingLeft }) => `padding-left: ${paddingLeft ? `${paddingLeft}px` : theme.fontSize[2]}`};
  ${({ theme, paddingRight }) => `padding-right: ${paddingRight ? `${paddingRight}px` : theme.fontSize[2]}`};
  ${({ fluid }) => `
    ${fluid && `
    width: 100%;
    margin-right: 0;
    `};
  `};
`;

type DatePickerInputProps = {
  id: string;
  label?: ReactNode;
  value?: string;
  errors?: string[];
  onChange: (date: Date | string) => void;
  labelStyles?: string;
  disabled?: boolean;
  touchOverride?: boolean;
  required?: boolean;
  small?: boolean;
  xs?: boolean;
  inline?: boolean;
  maxDate?: Date | null;
  isTooltipError?: boolean;
  showDay?: boolean;
  resetValue?: boolean;
  fluid?: boolean;
};

export function DatePickerInput({
  id,
  label,
  value,
  errors = [],
  onChange,
  labelStyles = '',
  disabled = false,
  touchOverride = false,
  required = false,
  small = false,
  inline = false,
  xs = false,
  maxDate = null,
  isTooltipError = false,
  showDay = false,
  resetValue = false,
  fluid = false,
}: DatePickerInputProps) {
  const [touched, setTouched] = useState(false);
  const [userInputCount, setUserInputCount] = useState(0);
  const hasError = (touched || touchOverride) && !!errors.length;
  const inputMonthRef = useRef<HTMLInputElement>(null);
  const inputYearRef = useRef<HTMLInputElement>(null);
  const inputDayRef = useRef<HTMLInputElement>(null);

  const { isDisabled } = useFormConfigurationContext(disabled);

  const validateDateFormat = (dateString?: string): boolean => {
    if (!isValidDate(dateString, DateFormatStringEnum.ISO8601)) {
      return false;
    }

    return true;
  };

  const extractDate = (dateString: string): { month: string, year: string, day: string } | null => {
    const formattedDate = new Date(dateString);

    if (Number.isNaN(formattedDate.getTime())) {
      return null; // Invalid date
    }

    const [year, month, day] = dateString.split('T')[0].split('-');

    return { month, year, day };
  };

  const addLeadingZero = (v: string, isBlurEvent: boolean, format: string) => (((format === 'MM' || format === 'DD') && isBlurEvent) ? NumericFormat.addLeadingZero(v) : v);

  const updateInputValue = (format: string, v: string) => {
    switch (format) {
      case 'MM': {
        if (inputMonthRef.current) {
          inputMonthRef.current.value = v;
        }
        break;
      }
      case 'DD': {
        if (inputDayRef.current) {
          inputDayRef.current.value = v;
        }
        break;
      }
      case 'YYYY': {
        if (inputYearRef.current) {
          inputYearRef.current.value = v;
        }
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (resetValue) {
      updateInputValue('MM', '');
      updateInputValue('YYYY', '');
      setTouched(false);

      if (showDay) {
        updateInputValue('DD', '');
        setTouched(false);
      }

      onChange('');
    }
  }, [resetValue, showDay, inputMonthRef?.current?.value, inputYearRef?.current?.value, inputDayRef?.current?.value]);

  useEffect(() => {
    if (inputMonthRef?.current?.value && inputYearRef?.current?.value) {
      setTouched(true);
    }
  }, [inputMonthRef?.current?.value, inputYearRef?.current?.value]);

  useEffect(() => {
    const isValidDateFormat = validateDateFormat(value);

    if (isValidDateFormat && value && !touched) {
      const extracted = extractDate(value);
      if (extracted) {
        const { day, month, year } = extracted;
        if (inputDayRef?.current) {
          inputDayRef.current.value = addLeadingZero(day, true, 'DD');
        }

        if (inputMonthRef?.current) {
          inputMonthRef.current.value = addLeadingZero(month, true, 'MM');
        }

        if (inputYearRef?.current) {
          inputYearRef.current.value = year;
        }
      }
    }
  }, [value, touched]);

  const validateYear = (yearValue: string): boolean => {
    const yearRegex = /^(19|20)\d{2}$/; // Regular expression to validate the year

    if (!yearRegex.test(yearValue)) {
      return false;
    }

    const year = parseInt(yearValue, 10);

    // Additional validation criteria if needed
    if (year < 1900 || year > 2099) {
      return false;
    }

    return true;
  };

  const validateDate = (day: number, month: number, year: number): boolean => moment(`${day}/${month}/${year}`, 'DD/MM/YYYY').utc().isValid();

  const processDate = ({
    fullDateValue,
    formattedNewDate,
    yearValue,
    monthValue,
    dayValue,
  }: {
    fullDateValue: string;
    formattedNewDate: Date;
    yearValue?: string;
    monthValue?: string;
    dayValue?: string;
    inputValue?: string;
  }) => {
    const formattedDate = initDate(fullDateValue, true).format(DateFormatStringEnum.ISO8601);
    const checkValidMonth = monthValue && monthValue.length > 2 ? '' : formattedDate;
    const checkValidYear = validateYear(yearValue ?? '') ? checkValidMonth : '';
    const checkValidDate = validateDate(Number(dayValue), Number(monthValue), Number(yearValue)) ? checkValidYear : '';

    return maxDate && formattedNewDate >= maxDate ? '' : checkValidDate;
  };

  const handleFocusEvent = (format?: string, dayVal?: string, monthVal?: string) => {
    if (showDay) {
      if (format === 'DD' && dayVal?.length === 2 && !Number.isNaN(Number(dayVal))) {
        inputMonthRef?.current?.focus();
      }
    }

    if (format === 'MM' && monthVal?.length === 2 && !Number.isNaN(Number(monthVal))) {
      inputYearRef?.current?.focus();
    }
  };

  const setInputMaxLength = (e: React.ChangeEvent<HTMLInputElement>, length: number) => {
    const maxLength = length; // Maximum length allowed
    const inputValue = e.target.value;

    if (inputValue.length > maxLength) {
      e.target.value = inputValue.slice(0, maxLength);
    }
  };

  const preventNegativeValues = (e: React.KeyboardEvent<HTMLInputElement>) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault();

  const handleDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    format: string,
    isBlurEvent: boolean = false,
  ) => {
    const monthValue = inputMonthRef?.current?.value;
    const yearValue = inputYearRef?.current?.value;
    const dayValue = inputDayRef?.current?.value;
    const inputValue = e.target.value;

    const formattedMonthValue = addLeadingZero(monthValue ?? '', isBlurEvent, format);
    const formattedDayValue = showDay ? addLeadingZero(dayValue ?? '', isBlurEvent, format) : '01';

    updateInputValue('MM', formattedMonthValue ?? '');
    updateInputValue('DD', formattedDayValue ?? '');

    const fullDateValue = `${formattedMonthValue}/${formattedDayValue}/${yearValue}`;
    const formattedNewDate = new Date(fullDateValue);

    const data = processDate({
      fullDateValue,
      formattedNewDate,
      yearValue,
      monthValue: formattedMonthValue,
      dayValue: formattedDayValue,
      inputValue,
    });

    onChange(data ?? '');

    const handleBlurEvent = () => {
      if (!monthValue && yearValue && userInputCount < 2) {
        setUserInputCount(userInputCount + 1);
        setTouched(true);
      }
    };

    if (isBlurEvent) {
      handleBlurEvent();
    } else {
      handleFocusEvent(format, dayValue, monthValue);
    }
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>, format: string) => {
    handleDateChange(e, format);
  };

  const handleOnBlur = (e: React.ChangeEvent<HTMLInputElement>, format: string) => {
    if (!e?.target?.value) {
      setTouched(true);
    }
    handleDateChange(e, format, true);
  };

  const handleClickOutside = () => {
    if (inputDayRef?.current?.value || inputMonthRef?.current?.value || inputYearRef?.current?.value) {
      setTouched(true);
    }
  };

  return (
    <Input
      id={id}
      label={label}
      errors={errors}
      hasError={hasError}
      labelStyles={labelStyles}
      required={required}
      inline={inline}
      isTooltipError={isTooltipError}
    >
      <DatePickerInputContainer>
        <OutsideClicker onClickOutside={handleClickOutside} ignoredEffectElements={[inputMonthRef.current, inputYearRef.current, inputDayRef.current]} isCheckCurrentElements>
          <DatePickerWrapper fluid={fluid}>
            {
              showDay && (
                <TextFieldInput
                  id={`${id}-day`}
                  hasError={hasError}
                  disabled={isDisabled}
                  onChange={(e) => handleOnChange(e, 'DD')}
                  onBlur={(e) => handleOnBlur(e, 'DD')}
                  placeholder="DD"
                  small={small}
                  xs={xs}
                  width={86}
                  ref={inputDayRef}
                  onKeyDown={preventNegativeValues}
                  onInput={(e: React.ChangeEvent<HTMLInputElement>) => setInputMaxLength(e, 2)}
                  type="number"
                  fluid={fluid}
                />
              )
            }
            <TextFieldInput
              id={`${id}-month`}
              hasError={hasError}
              disabled={isDisabled}
              onChange={(e) => handleOnChange(e, 'MM')}
              onBlur={(e) => handleOnBlur(e, 'MM')}
              placeholder="MM"
              small={small}
              xs={xs}
              width={86}
              ref={inputMonthRef}
              type="number"
              onKeyDown={preventNegativeValues}
              onInput={(e: React.ChangeEvent<HTMLInputElement>) => setInputMaxLength(e, 2)}
              fluid={fluid}
            />
            <TextFieldInput
              id={`${id}-year`}
              hasError={hasError}
              placeholder="YYYY"
              disabled={isDisabled}
              onChange={(e) => handleOnChange(e, 'YYYY')}
              onBlur={(e) => handleOnBlur(e, 'YYYY')}
              small={small}
              xs={xs}
              width={86}
              ref={inputYearRef}
              onKeyDown={preventNegativeValues}
              onInput={(e: React.ChangeEvent<HTMLInputElement>) => setInputMaxLength(e, 4)}
              type="number"
              fluid={fluid}
            />
          </DatePickerWrapper>
        </OutsideClicker>
      </DatePickerInputContainer>
    </Input>
  );
}
